"use client";

import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useUIStore } from "@/lib/store";
import { getProductsQueryOptions } from "@/lib/queries/products";
import { ProductsClient } from "./ProductsClient";
import { FiltersClient } from "./FiltersClient";
import { useApi } from "@/lib/hooks/useApi";
import { useSearchParams } from "next/navigation";
import { useCartStore } from "@/lib/store/cartStore";
import { PricingData, PricingPayload, Product } from "@/lib/models/product";
import { PricingResponse } from "@/lib/models/apiResponse";
import { useSession } from "next-auth/react";
import {
  isPublicSession,
  isAdminSession,
  isSapLinkedSession,
  isSupplierSession,
  canViewPrivilegedPrices,
  isInternalSession,
} from "@/lib/utils/pricingVisibility";
import { getDisplayPricing } from "@/lib/utils/generatePrice";
const PAGE_SIZE = 32;

interface productSend {
  sku: string;
}
/**
 * Products Page
 * Client Component that handles the entire products section
 * Coordinates filters and products display
 */
import { Suspense } from "react";
export function ProductsContent() {
  const { apiRequest } = useApi("");
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [productsWithPrices, setProductsWithPrices] = useState<Product[]>([]);
  const [loadingPrices, setLoadingPrices] = useState<boolean>(false);
  // Id de la última petición de precios: si cambia la página o el filtro
  // mientras una sigue pendiente, su respuesta se descarta al llegar.
  const pricingRequestId = useRef(0);
  const setLoading = useUIStore((state) => state.setLoading);
  const searchParams = useSearchParams();
  const orderBy = searchParams.get("sort") ?? "relevancia";
  const { productKey, setExchangeRate } = useCartStore();

  const { data: session } = useSession();
  const isPublic = isPublicSession(session);
  const isAdmin = isAdminSession(session);
  const isLinked = isSapLinkedSession(session);
  const isSupplier = isSupplierSession(session);
  const isInternal = isInternalSession(session);

  // Misma regla que ProductCard.tsx para decidir si se le muestra el precio
  // de lista pública o el precio privilegiado del cliente - la visibilidad
  // de marca de proveedor varía por producto, así que se evalúa por cada uno.
  function isDefaultCustomerForProduct(product: Product) {
    const canViewPrivilegedPrice = canViewPrivilegedPrices(
      session,
      product.marcaCode,
    );
    return session === null || session === undefined || !canViewPrivilegedPrice;
  }

  const queryParam = searchParams.get("query") || "";
  const searchKey = searchParams.toString();

  // Resetear página cuando cambia cualquier parámetro de la URL (filtros, categorías, etc.)
  useEffect(() => {
    setCurrentPage(0);
  }, [searchKey]);

  useEffect(() => {
    setSelectedFilters({});
  }, [queryParam]);

  const params = Object.fromEntries(searchParams.entries());

  const {
    data: productsData,
    isError,
    error,
    isLoading: isLoadingProducts,
  } = useQuery(
    getProductsQueryOptions(
      params,
      currentPage,
      PAGE_SIZE,
      selectedFilters,
    ),
  );

  async function handleGetPrices() {
    const requestId = ++pricingRequestId.current;
    setLoading(true);

    const dataToSend: productSend[] = (productsData?.data?.items || []).map(
      (product: Product) => ({ sku: product.sku }),
    );

    const pricingPayload: PricingPayload = {
      items: dataToSend,
    };

    setLoadingPrices(true);

    apiRequest<PricingResponse>("", {
      method: "post",
      data: pricingPayload,
      url: `${process.env.NEXT_PUBLIC_API_URL}/pricing/calculate-batch`,
      loadingMessage: "Cargando productos...",
    })
      .then((response) => {
        if (requestId !== pricingRequestId.current) return;
        const pricingData = response?.data?.precios || [];

        // No mutar productsData.data.items directamente: es el mismo array
        // que vive en la cache de TanStack Query, y mutar objetos cacheados
        // in place puede confundir su comparación de referencias/structural
        // sharing y disparar renders extra en cascada. Se construye una
        // copia con el precio ya asignado en su lugar.
        const pricedItems = (productsData?.data?.items || []).map(
          (product: Product) => {
            const priceItem = pricingData.find(
              (p: PricingData) => p.sku === product.sku,
            );
            return priceItem ? { ...product, price: priceItem } : product;
          },
        );

        const tasa = pricingData.find(
          (p: PricingData) => typeof p.tasaCambio === "number",
        )?.tasaCambio;
        if (tasa) {
          setExchangeRate(tasa);
        }
        filterByPrice(pricedItems, true);
      })
      .catch((err) => {
        if (requestId !== pricingRequestId.current) return;
        // Si el cálculo de precios falla (p. ej. no hay tasa de cambio) no se
        // deben ocultar los productos: se muestran con el precio de lista 1
        // que ya trae la búsqueda (las cards usan priceList1 cuando el
        // producto no trae precio calculado).
        console.error("Error al calcular precios del listado:", err);
        filterByPrice(productsData?.data?.items, false);
      })
      .finally(() => {
        if (requestId !== pricingRequestId.current) return;
        setLoading(false);
        setLoadingPrices(false);
      });
  }

  function filterByPrice(
    items: Product[] | undefined = productsData?.data?.items,
    pricesLoaded: boolean = true,
  ) {
    if (!items) return;

    let sorted = [...items];

    // Elasticsearch ordena por el priceList1 estático sincronizado desde
    // pricing-service; para NAME_ASC/NAME_DESC eso ya es correcto (el nombre
    // no depende del cliente). Para precio, el valor real ya calculado por
    // /pricing/calculate-batch (arriba, en handleGetPrices) puede diferir
    // del estático - así que se reordena aquí con el mismo precio que se
    // muestra en cada card (getDisplayPricing).
    if (orderBy === "PRICE_LIST_1_ASC" || orderBy === "PRICE_LIST_1_DESC") {
      sorted.sort((a, b) => {
        const priceA =
          getDisplayPricing(a, isDefaultCustomerForProduct(a), productKey)
            ?.mainPrice ?? 0;
        const priceB =
          getDisplayPricing(b, isDefaultCustomerForProduct(b), productKey)
            ?.mainPrice ?? 0;
        return orderBy === "PRICE_LIST_1_ASC"
          ? priceA - priceB
          : priceB - priceA;
      });
    }

    // Filtrar solo productos con sección PRECIO_LIQUIDACION si
    // inStockWarehouse05Only=true y no es usuario público, pero solo si los precios
    // fueron calculados exitosamente.
    if (
      pricesLoaded &&
      !isPublic &&
      params.inStockWarehouse05Only === "true"
    ) {
      sorted = sorted.filter((product: Product) =>
        product.price?.secciones?.some((s) => s.tipo === "PRECIO_LIQUIDACION"),
      );
    }
    setProductsWithPrices(sorted);
  }

  useEffect(() => {
    if (productsData?.data?.items && productsData.data.items.length > 0) {
      handleGetPrices();
    } else if (productsData?.data?.items && productsData.data.items.length === 0) {
      // Una petición pendiente de la búsqueda anterior no debe rellenar la lista vacía.
      pricingRequestId.current++;
      setLoading(false);
      setLoadingPrices(false);
      setProductsWithPrices([]);
    }
  }, [productsData?.data?.items]);

  const handleFilterChange = (filters: Record<string, string[]>) => {
    setSelectedFilters(filters);
    setCurrentPage(0);
  };

  if (isError) {
    return (
      <div className="p-4 flex items-center justify-center min-h-96">
        <div className="text-center">
          <p className="text-red-500 font-semibold">
            Error al cargar productos
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            {error instanceof Error
              ? error.message
              : "Intenta nuevamente más tarde"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full py-4">
      <div className="flex flex-col md:flex-row gap-3 sm:gap-4">
        <div className="w-full md:w-55 lg:w-60 xl:w-64 md:flex-shrink-0 md:sticky md:top-0 md:self-start">
          <FiltersClient
            facets={productsData?.data?.facets}
            onFilterChange={handleFilterChange}
          />
        </div>
        <div className="flex-1">
          <ProductsClient
            products={productsWithPrices || []}
            total={productsData?.data?.total || 0}
            currentPage={currentPage || 0}
            pageSize={PAGE_SIZE}
            isLoading={isLoadingProducts}
            onPageChange={setCurrentPage}
            loadingPrices={loadingPrices}
          />
        </div>
      </div>
    </div>
  );
}

export default function ProductsPageClient() {
  return (
    <Suspense fallback={<div>Cargando productos...</div>}>
      <ProductsContent />
    </Suspense>
  );
}
